import React, { useRef, useState, useLayoutEffect, useEffect, useCallback } from 'react';

import { useSpring, animated } from 'react-spring';

export type AnimatedCollapseProps = Readonly<{
  isOpen: boolean;
  direction?: 'vertical' | 'horizontal';
  children: React.ReactNode;
  /** the spring factor to control the speed of the animation (sort of like time) */
  springFactor?: number;
  /** set to true to prevent the animation from bouncing */
  clampBounce?: boolean;
  opacityFade?: boolean;
  animateOnMount?: boolean;
}>;

export const AnimatedCollapse = ({
  isOpen,
  direction = 'vertical',
  children,
  springFactor = 1,
  clampBounce = false,
  opacityFade = false,
  animateOnMount = false,
}: AnimatedCollapseProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isFirstRender, setIsFirstRender] = useState(true);

  const [measuredDimension, setMeasuredDimension] = useState(0);

  const measureDimension = useCallback(() => {
    if (contentRef.current) {
      const newDimension =
        direction === 'horizontal'
          ? contentRef.current.scrollWidth
          : contentRef.current.scrollHeight;

      if (newDimension !== measuredDimension) {
        setMeasuredDimension(newDimension);
      }
    }
  }, [direction, measuredDimension]);

  useLayoutEffect(() => {
    if (isFirstRender && !animateOnMount) {
      setTimeout(() => setIsFirstRender(false), 0);
    }

    // Force a measurement after the initial render and after content changes
    requestAnimationFrame(() => {
      measureDimension();
    });
  }, [children, isOpen, direction, isFirstRender, animateOnMount, measureDimension]);

  // Set up a resize observer to handle dynamic content changes
  useEffect(() => {
    if (!contentRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      measureDimension();
    });

    resizeObserver.observe(contentRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [direction, measureDimension]);

  const animationStyles = useSpring({
    [direction === 'horizontal' ? 'width' : 'height']: isOpen ? measuredDimension : 0,
    opacity: opacityFade ? (isOpen ? 1 : 0) : 1,
    immediate: isFirstRender,
    config: {
      tension: 300 * springFactor,
      friction: 30 * springFactor,
      clamp: clampBounce,
    },
  });

  return (
    <animated.div style={{ overflow: 'hidden', ...animationStyles }}>
      <div ref={contentRef}>{children}</div>
    </animated.div>
  );
};
